/**
 * useNavCounts — the CRM shell's navigation badge counts, extracted from
 * Sidebar so the desktop sidebar and the mobile bottom tab bar share ONE
 * fetch cycle (CRMApp's NavCountsRoot calls it once, above the routes and the
 * floating overlays, and everything reads it through NavCountsContext).
 *
 * Conversation counts come from GET /api/crm/contacts/stats
 * (stats.conversations), the Today badge from GET /api/crm/worklist?counts=true,
 * refreshed every 60s and immediately on the STATS_CHANGED_EVENT window event
 * (pages dispatch it after state-changing PATCHes). A failed fetch just nulls
 * the badges — navigation must never break because a count endpoint hiccuped.
 *
 * This context is the ONE source for any conversation count shown in more
 * than one place (sidebar badge, mobile tab, a page header) — a page reads it
 * instead of opening its own /contacts/stats poll, so two surfaces can never
 * quietly disagree about the same number.
 *
 * The Active conversations badge uses the needs_reply count (leads who
 * replied and still owe us a response), not the full active count — it
 * mirrors the tab's default "Awaiting my reply" filter, so the badge is the
 * actionable backlog rather than every conversation that ever got a reply.
 *
 * Review §C14: the referral collision queue got a badge here because it had no
 * proactive surface at all. It lived behind three clicks in CRM Settings, its
 * only indicator was rendered INSIDE the panel from the list the panel had
 * already loaded, and the only push channel was a server-side log alert that
 * reaches ops — not the recruiter who has to make the call the patient was
 * promised. It is fetched ONLY for CRM admins because the endpoint is
 * `requireCrmAdmin`; for everyone else the badge stays null rather than
 * spending a poll on a guaranteed 403.
 *
 * Outreach lists (docs/crm-outreach-lists-plan.md §B5/§B7) ride the SAME worklist
 * response: its `outreach` block is the sidebar badge, Today's "Your outreach
 * lists" section and the new-list notice — one poll, no second interval. The
 * block is null when the api predates outreach lists (or the fetch failed), and
 * every consumer reads null as "hide", never as "zero lists".
 *
 * Mentions (docs/plans/crm-report-builder-search-collab-plan.md B3.4) ride the
 * same worklist response too: its `buckets.mentions` is `{ count, newest }` for
 * the viewer's unread @mentions and replies. The count is already inside the
 * Today badge (total_actionable); `newest` lets the corner card tell a new
 * mention from one it already showed, and the lead page reads the count to
 * mark a lead's mentions read when it is opened. No second request, no new
 * interval; null when the api predates mentions or the fetch failed.
 *
 * THE COLLISION COUNT IS THE ONE BADGE WHOSE SOURCE COULD CARRY PHI, and it is
 * handled accordingly: it goes through `fetchCollisionSummary`, which asks a
 * count-only endpoint (see portalReferrals), and it is the only fetch here that
 * is suspended while the tab is hidden. Everything else on this tick is already
 * an aggregate. It is also the ONLY poll of that data in the app — Today's
 * CollisionTriageCard reads the value out of the context below rather than
 * running a second interval of its own against the same rows.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { LifecycleStats, type WorklistMentionsBucket } from './types';
import { STATS_CHANGED_EVENT, useIsCRMAdmin } from './lib';
import { fetchCollisionSummary, type CollisionSummary } from './portalReferrals';
import { parseWorklistOutreach } from './outreach/outreachSurfaces';
import { parseMentionsBucket } from './mentions/mentionLogic';
import type { WorklistOutreachCounts } from './outreach/types';

export interface ConversationCounts {
  /** Leads who replied and still owe us a response — drives the Active badge. */
  needs_reply: number;
  awaiting: number;
  /** Every lead who has replied at least once (a superset of needs_reply) —
   *  the "All active conversations" header count. */
  active: number;
  /** Recruiter-archived conversations (an inbound reply auto-reopens them). */
  archived: number;
}

export interface NavCounts {
  conversations: ConversationCounts | null;
  today: number | null;
  fax: number | null;
  voicemail: number | null;
  /**
   * Escalated questions + eligibility decisions the texting agent is holding for
   * a person (D-7). It is the `agent_review` bucket of the worklist counts this
   * hook ALREADY fetches for the Today badge — no second request and no change
   * of cadence, because the number is a slice of a response we are paying for
   * anyway. Null when the bucket is absent or the fetch failed.
   */
  agentReview: number | null;
  /**
   * Leads left behind (the Safety-net lane, docs/crm-safety-net-plan.md §5):
   * network-wide open catches deduped to leads. Like agentReview, a slice of
   * the worklist counts response this hook already fetches — no extra request.
   */
  safetyNet: number | null;
  /** Referral name mismatches awaiting a phone call (CRM admins only). */
  portalCollisions: CollisionSummary | null;
  /**
   * My outreach lists — servable/urgent/waiting totals plus one row per list I
   * am working. Sliced from the worklist counts response like agentReview; null
   * when the block is absent (an api without outreach lists) or the fetch
   * failed, which hides the sidebar item and the Today section.
   */
  outreach: WorklistOutreachCounts | null;
  /**
   * My unread @mentions and replies: the `mentions` bucket of the worklist
   * counts this hook ALREADY fetches, `{ count, newest: { id, created_at } }`
   * (mentionLogic parseMentionsBucket). Null when the bucket is absent (an api
   * without mentions) or the fetch failed, and every consumer reads null as
   * "unknown, show nothing", never as "no mentions". Optional only so a
   * hand-built context (a test's provider value) may leave it out.
   */
  mentions?: WorklistMentionsBucket | null;
}

const EMPTY: NavCounts = {
  conversations: null, today: null, fax: null, voicemail: null, agentReview: null,
  safetyNet: null, portalCollisions: null, outreach: null, mentions: null,
};

export default function useNavCounts(): NavCounts {
  const [counts, setCounts] = useState<NavCounts>(EMPTY);
  const isCrmAdmin = useIsCRMAdmin();

  useEffect(() => {
    let alive = true;
    let collisionsAskedAt = 0;
    const loadCollisions = async () => {
      if (!isCrmAdmin) return;
      collisionsAskedAt = Date.now();
      try {
        const summary = await fetchCollisionSummary();
        if (alive) setCounts((prev) => ({ ...prev, portalCollisions: summary }));
      } catch {
        if (alive) setCounts((prev) => ({ ...prev, portalCollisions: null }));
      }
    };
    const load = async () => {
      try {
        const res = await api.get('/api/crm/contacts/stats');
        const stats = res.data as LifecycleStats | null;
        const conv = stats?.conversations;
        if (alive) {
          setCounts((prev) => ({
            ...prev,
            conversations: conv
              ? { needs_reply: conv.needs_reply, awaiting: conv.awaiting, active: conv.active, archived: conv.archived }
              : null,
            fax: typeof stats?.fax_needs_match === 'number' ? stats.fax_needs_match : null,
            voicemail: typeof stats?.voicemail_unheard === 'number' ? stats.voicemail_unheard : null,
          }));
        }
      } catch {
        if (alive) setCounts((prev) => ({ ...prev, conversations: null, fax: null, voicemail: null }));
      }
      try {
        const res = await api.get('/api/crm/worklist', { params: { counts: 'true' } });
        const agentReview = res.data?.buckets?.agent_review?.count;
        const safetyNet = res.data?.safety_net?.count;
        if (alive) {
          setCounts((prev) => ({
            ...prev,
            today: typeof res.data?.total_actionable === 'number' ? res.data.total_actionable : null,
            agentReview: typeof agentReview === 'number' ? agentReview : null,
            safetyNet: typeof safetyNet === 'number' ? safetyNet : null,
            outreach: parseWorklistOutreach(res.data?.outreach),
            mentions: parseMentionsBucket(res.data?.buckets?.mentions),
          }));
        }
      } catch {
        if (alive) {
          setCounts((prev) => ({
            ...prev, today: null, agentReview: null, safetyNet: null, outreach: null, mentions: null,
          }));
        }
      }
      // Suspended while the tab is hidden, and caught up the moment it comes
      // back. A CRM left open on a second monitor for a shift should not be
      // asking about referral patients four hundred times over.
      if (!document.hidden) await loadCollisions();
    };
    load();
    const timer = window.setInterval(load, 60_000);
    // Only the collision summary is refetched on focus — the other two counts
    // are on the 60s tick and re-running all three on every alt-tab would trade
    // one background poll for a burst of foreground ones. Floored at 20s for the
    // same reason: someone flicking between windows must not be able to turn
    // "catch up when you come back" into a request per keystroke.
    const onVisible = () => {
      if (!document.hidden && Date.now() - collisionsAskedAt >= 20_000) loadCollisions();
    };
    window.addEventListener(STATS_CHANGED_EVENT, load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isCrmAdmin]);

  return counts;
}

/**
 * The shell's counts, shared downward.
 *
 * CRMLayout runs `useNavCounts` once and publishes the result here so a page can
 * render a count without opening a second poll against the same endpoint. That
 * matters most for the collision summary, whose fallback source carries patient
 * names — see portalReferrals — but it is the right shape for any badge: one
 * fetch cycle, one answer, no two indicators disagreeing about the same number.
 *
 * The default is the empty set rather than a thrown "missing provider", because
 * every consumer already treats null as "nothing to show" and a badge is never
 * worth a blank screen.
 */
export const NavCountsContext = createContext<NavCounts>(EMPTY);

export function useSharedNavCounts(): NavCounts {
  return useContext(NavCountsContext);
}
